import React from 'react';
import { motion } from 'framer-motion';
import { Database, Cloud, LayoutGrid, ShieldCheck, Share2, PieChart, ArrowRight, Activity, Server, Lock } from 'lucide-react';
export function ArchitectureDiagram() {
  return <section className="relative w-full bg-[#16120F] py-24 md:py-32 overflow-hidden min-h-[900px] flex items-center" data-id="element-1119" data-mp="3svurylyarti6">
      {/* Background SVG Lines */}
      <div className="absolute inset-0 z-0 opacity-60" data-id="element-1120" data-mp="3oxndjxrb34a5">
        <svg className="w-full h-full" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" data-id="element-1121" data-mp="3jwzkl6jiodda">
          <defs data-id="element-1122" data-mp="3f46gmkvvh929">
            <linearGradient id="glow" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-1123" data-mp="3f3endgpqznf5">
              <stop offset="0%" stopColor="#EA580C" stopOpacity="0" data-id="element-1124" data-mp="3a6xuyabimmw9" />
              <stop offset="50%" stopColor="#EA580C" stopOpacity="1" data-id="element-1125" data-mp="35834qnb7xw24" />
              <stop offset="100%" stopColor="#EA580C" stopOpacity="0" data-id="element-1126" data-mp="315q8cpid3ai0" />
            </linearGradient>
            <filter id="blur" data-id="element-1127" data-mp="2w6n7lh9ezqfu">
              <feGaussianBlur stdDeviation="4" data-id="element-1128" data-mp="2w7ogpg6ve2ux" />
            </filter>
          </defs>

          {/* Base Lines */}
          <path d="M -100 400 L 200 400 L 300 200 L 500 200 L 600 400 L 800 400" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-1129" data-mp="21tgcjnns5zj6" />
          <path d="M 500 200 L 700 200 L 800 100 L 1000 100" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-1130" data-mp="26n1cvmcuj5lj" />
          <path d="M 800 400 L 1000 400 L 1100 600 L 1300 600" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-1131" data-mp="1srql4htwf0le" />
          <path d="M 400 700 L 600 700 L 700 500 L 800 500" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-1132" data-mp="2kkpv0mfj3ymw" />
          <path d="M 800 500 L 800 700 L 1000 700" fill="none" stroke="#332A24" strokeWidth="2" data-id="element-1133" data-mp="2pjkc9jxulf4w" />

          {/* Animated Glowing Lines */}
          <motion.path d="M -100 400 L 200 400 L 300 200 L 500 200" fill="none" stroke="url(#glow)" strokeWidth="3" filter="url(#blur)" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-1134" data-mp="2bj9mylc6s8lu" />
          <motion.path d="M 500 200 L 700 200 L 800 100 L 1000 100" fill="none" stroke="url(#glow)" strokeWidth="3" filter="url(#blur)" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear',
          delay: 1
        }} data-id="element-1135" data-mp="36tejgye7eebt" />
          <motion.path d="M 800 400 L 1000 400 L 1100 600 L 1300 600" fill="none" stroke="url(#glow)" strokeWidth="3" filter="url(#blur)" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'linear',
          delay: 2
        }} data-id="element-1136" data-mp="2o36mky13kk42" />
        </svg>
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 w-full grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-12" data-id="element-1137" data-mp="cbt6jabez2ew">
        {/* Left Content */}
        <div className="flex flex-col justify-center pt-10 lg:pt-0" data-id="element-1138" data-mp="1q9dyp84110zj">
          <h2 className="text-5xl md:text-7xl font-title font-medium tracking-tight text-white leading-[1.05] mb-6" data-id="element-1139" data-mp="1q71lf897rest">
            Connect
            <br data-id="element-1140" data-mp="1zyfeoyaknidi" />
            <span className="text-[#EA580C]" data-id="element-1141" data-mp="200098nwvz1bd">Everything.</span>
            <br data-id="element-1142" data-mp="240j1uk6lsbem" />
            Architect
            <br data-id="element-1143" data-mp="23zhg9lryo6in" />
            Anything.
          </h2>
          <p className="text-lg text-[#9C8E82] leading-relaxed mb-10 max-w-sm" data-id="element-1144" data-mp="17a9ss87s7nc3">
            The intelligent infrastructure platform that connects your data,
            systems, and teams—seamlessly.
          </p>
          <div className="flex items-center gap-6" data-id="element-1145" data-mp="1gb7xyttyi0w9">
            <button className="px-6 py-3 rounded-full bg-[#EA580C] hover:bg-[#d24e09] text-white font-semibold transition-colors flex items-center gap-2" data-id="element-1146" data-mp="1g9n3t5bk0jyx">
              Request a Demo
              <ArrowRight size={18} data-id="element-1147" data-mp="2rpbry00jnp1b" />
            </button>
            <button className="text-[#EA580C] font-medium hover:text-white transition-colors flex items-center gap-2" data-id="element-1148" data-mp="2woy6ccs3zbbp">
              Explore Platform
              <ArrowRight size={18} data-id="element-1149" data-mp="2v8kky32e52go" />
            </button>
          </div>
        </div>

        {/* Right Diagram Area */}
        <div className="relative h-[800px] hidden lg:block" data-id="element-1150" data-mp="2qbapo6sblwkx">
          {/* Data Sources Card */}
          <div className="absolute top-[50px] left-[100px] w-[280px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-1151" data-mp="346mlyim4jv3y">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-1152" data-mp="3e5vaux24ujjg">
              <Database size={14} className="text-[#EA580C]" data-id="element-1153" data-mp="397aitkx1b663" />
              Data Sources
            </div>
            <div className="space-y-2" data-id="element-1154" data-mp="3nvwxdkeu7tw9">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-1155" data-mp="3ixvi7akj8eq2">
                <Cloud size={18} className="text-neutral-300" data-id="element-1156" data-mp="rh2p76m7wpk" />
                <div data-id="element-1157" data-mp="pxjp8tkr7k3">
                  <div className="text-sm font-medium text-white" data-id="element-1158" data-mp="3ryl85xn43avv">
                    Cloud Storage
                  </div>
                  <div className="text-xs text-neutral-500" data-id="element-1159" data-mp="fefpupyfw4yh">Amazon S3</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-1160" data-mp="kchctlyzkof8">
                <Server size={18} className="text-neutral-300" data-id="element-1161" data-mp="6dhmm9vts1gy" />
                <div data-id="element-1162" data-mp="af34yar4p7yj">
                  <div className="text-sm font-medium text-white" data-id="element-1163" data-mp="aebaj399pg9u">Database</div>
                  <div className="text-xs text-neutral-500" data-id="element-1164" data-mp="3stc3m32ieicy">PostgreSQL</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-1165" data-mp="1fzkb2cbckhx">
                <LayoutGrid size={18} className="text-neutral-300" data-id="element-1166" data-mp="3iwqwnc6xix6h" />
                <div data-id="element-1167" data-mp="3ntzjvr6xprel">
                  <div className="text-sm font-medium text-white" data-id="element-1168" data-mp="3nvka54m7uo6n">
                    Applications
                  </div>
                  <div className="text-xs text-neutral-500" data-id="element-1169" data-mp="39vjblvtfbsuc">12 Connectors</div>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-1170" data-mp="1kdcd57ts6oxs">
              View all sources <ArrowRight size={12} data-id="element-1171" data-mp="1flkvjmslrtjs" />
            </div>
          </div>

          {/* Data Warehouse Card */}
          <div className="absolute top-[250px] left-[450px] w-[300px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl z-20" data-id="element-1172" data-mp="1alq67noh7c0u">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-1173" data-mp="23bf9k7onro5m">
              <Database size={14} className="text-[#EA580C]" data-id="element-1174" data-mp="1ydwwc53lymrt" />
              Data Warehouse
            </div>
            <div className="mb-6" data-id="element-1175" data-mp="1u9yzk11r9o1x">
              <div className="text-sm font-medium text-white" data-id="element-1176" data-mp="1pcz1acb5dpld">
                Analytics Warehouse
              </div>
              <div className="text-xs text-neutral-500" data-id="element-1177" data-mp="iwwr5t9t84su">Snowflake</div>
            </div>
            {/* Hex Grid Graphic Placeholder */}
            <div className="h-32 flex items-center justify-center mb-4" data-id="element-1178" data-mp="dw8qefvs49lf">
              <div className="grid grid-cols-5 gap-2 opacity-80" data-id="element-1179" data-mp="2plyg7q77zhux">
                {Array.from({
                length: 25
              }).map((_, i) => <div key={i} className={`w-3 h-3 rounded-full ${i % 3 === 0 ? 'bg-[#EA580C]' : 'bg-white/20'}`} data-id="element-1180" data-mp="2uhvza49vb2be" data-mpi={i} />)}
              </div>
            </div>
            <div className="flex items-center justify-between text-xs" data-id="element-1181" data-mp="33dbyl0te69od">
              <div className="flex items-center gap-1.5 text-emerald-400" data-id="element-1182" data-mp="38bn6v741gc4h">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" data-id="element-1183" data-mp="38dr9llq7b536" />
                Healthy
              </div>
              <div className="text-neutral-500" data-id="element-1184" data-mp="3i9uako9bgxoj">99.9% uptime</div>
            </div>
          </div>

          {/* Data Pipelines Card */}
          <div className="absolute top-[40px] right-[20px] w-[280px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-1185" data-mp="3ma4oqo79iueh">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-1186" data-mp="1rx57kqn8hs53">
              <Activity size={14} className="text-[#EA580C]" data-id="element-1187" data-mp="190czq6yzsfj7" />
              Data Pipelines
            </div>
            <div className="space-y-2" data-id="element-1188" data-mp="142lajkpqf7ip">
              <div className="p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-1189" data-mp="141jq4pmtt8ob">
                <div className="text-sm font-medium text-white mb-1" data-id="element-1190" data-mp="1hsorbw5ue90r">
                  User Activity Pipeline
                </div>
                <div className="flex items-center justify-between" data-id="element-1191" data-mp="1dtz6jiih5p73">
                  <span className="text-xs text-[#EA580C]" data-id="element-1192" data-mp="q2k9a45k1y2p">Real-time</span>
                  <svg width="60" height="15" viewBox="0 0 60 15" fill="none" data-id="element-1193" data-mp="l3z9g5u7tges">
                    <path d="M0 10 Q 10 0, 20 10 T 40 10 T 60 5" stroke="#EA580C" strokeWidth="1.5" fill="none" data-id="element-1194" data-mp="z38cs4xbog1c" />
                  </svg>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-1195" data-mp="u5yo4rhrc8ek">
                <div className="text-sm font-medium text-white mb-1" data-id="element-1196" data-mp="2ajyog3kim02c">
                  ETL – Sales Data
                </div>
                <div className="flex items-center justify-between" data-id="element-1197" data-mp="2dzt8ugn3u81t">
                  <span className="text-xs text-blue-400" data-id="element-1198" data-mp="2dyi4ofpqyya6">Scheduled</span>
                  <svg width="60" height="15" viewBox="0 0 60 15" fill="none" data-id="element-1199" data-mp="2i268qof67bqo">
                    <path d="M0 5 Q 15 15, 30 5 T 60 10" stroke="#60A5FA" strokeWidth="1.5" fill="none" data-id="element-1200" data-mp="242f2j9uwazn8" />
                  </svg>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-white/5 border border-white/5" data-id="element-1201" data-mp="2wqau605ly1d5">
                <div className="text-sm font-medium text-white mb-1" data-id="element-1202" data-mp="2wsdtjasnh6uj">
                  Event Processing
                </div>
                <div className="flex items-center justify-between" data-id="element-1203" data-mp="2n0r9qq28xzg6">
                  <span className="text-xs text-[#EA580C]" data-id="element-1204" data-mp="2mydheoyyb1cb">Streaming</span>
                  <svg width="60" height="15" viewBox="0 0 60 15" fill="none" data-id="element-1205" data-mp="2rxzvf0mlslm6">
                    <path d="M0 12 Q 10 2, 20 12 T 40 8 T 60 12" stroke="#EA580C" strokeWidth="1.5" fill="none" data-id="element-1206" data-mp="3fo3y95rywhc4" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-1207" data-mp="3pdnz4crooqus">
              View all pipelines <ArrowRight size={12} data-id="element-1208" data-mp="3gd7ydb2brxtm" />
            </div>
          </div>

          {/* Governance Card */}
          <div className="absolute bottom-[100px] left-[150px] w-[260px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-1209" data-mp="3lcauu748ovpz">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-1210" data-mp="36lv2879hulhs">
              <ShieldCheck size={14} className="text-[#EA580C]" data-id="element-1211" data-mp="3bfzdh2pslfoo" />
              Governance
            </div>
            <div className="space-y-4" data-id="element-1212" data-mp="2xkx1pyy9kw3y">
              <div data-id="element-1213" data-mp="31pd3pp4rbjdw">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-1214" data-mp="31ouxq0salqss">
                  <span className="text-white" data-id="element-1215" data-mp="2noagh921031e">Data Quality</span>
                  <span className="text-neutral-400" data-id="element-1216" data-mp="2snncokesemgn">98%</span>
                </div>
                <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-1217" data-mp="2sppyes8wptfk">
                  <div className="h-full bg-[#EA580C] w-[98%]" data-id="element-1218" data-mp="2923lss8i5cej" />
                </div>
              </div>
              <div data-id="element-1219" data-mp="244v38qjfz6cr">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-1220" data-mp="2j018vceml35k">
                  <span className="text-white" data-id="element-1221" data-mp="2j1cfdjzggp0d">Compliance</span>
                  <span className="text-neutral-400" data-id="element-1222" data-mp="2dzwrlk5w84ea">100%</span>
                </div>
                <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-1223" data-mp="2s07hajvgvcxv">
                  <div className="h-full bg-[#EA580C] w-full" data-id="element-1224" data-mp="2n1w88bczx6gp" />
                </div>
              </div>
              <div data-id="element-1225" data-mp="31q95rcrqexdb">
                <div className="flex justify-between text-xs mb-1.5" data-id="element-1226" data-mp="31phbc59vf5om">
                  <span className="text-white" data-id="element-1227" data-mp="2wrpldgssdtn2">Access Control</span>
                  <span className="text-emerald-400" data-id="element-1228" data-mp="2wtissyb84hw4">Active</span>
                </div>
                <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-1229" data-mp="3argtx20jahto">
                  <div className="h-full bg-emerald-400 w-full" data-id="element-1230" data-mp="36qx0o9j2zyct" />
                </div>
              </div>
            </div>
            <div className="mt-5 flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-1231" data-mp="3dqpdlh6a5p9i">
              View governance <ArrowRight size={12} data-id="element-1232" data-mp="3npye6svni24u" />
            </div>
          </div>

          {/* Integrations Card */}
          <div className="absolute bottom-[40px] left-[480px] w-[240px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-1233" data-mp="3sihtpvdwf7ca">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-1234" data-mp="5cqisiv1o9xl">
              <Share2 size={14} className="text-[#EA580C]" data-id="element-1235" data-mp="abtnu3b0yg5k" />
              Integrations
            </div>
            <div className="flex items-center gap-3 mb-4" data-id="element-1236" data-mp="fbfzi8b7foc9">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-1237" data-mp="2c3g7fjsl78s0">
                <img src="https://cdn.simpleicons.org/slack/white" alt="Slack" className="w-5 h-5" data-id="element-1238" data-mp="2h2jetaw1hr35" />
              </div>
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-1239" data-mp="y7yf0xqxihpa">
                <img src="https://cdn.simpleicons.org/salesforce/white" alt="Salesforce" className="w-5 h-5" data-id="element-1240" data-mp="tg6kkc4njrtv" />
              </div>
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-1241" data-mp="pfwhp8c5phj8">
                <LayoutGrid size={18} className="text-white" data-id="element-1242" data-mp="khbinc8n53wd" />
              </div>
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-1243" data-mp="1h59cqdwwx32m">
                <Cloud size={18} className="text-white" data-id="element-1244" data-mp="1d5qs7j906u0b" />
              </div>
            </div>
            <div className="flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-1245" data-mp="184t8v3c9btx8">
              Manage integrations <ArrowRight size={12} data-id="element-1246" data-mp="138vxzcjrjfru" />
            </div>
          </div>

          {/* Analytics & BI Card */}
          <div className="absolute bottom-[120px] right-[40px] w-[260px] bg-[#221C18]/80 backdrop-blur-md border border-white/10 rounded-2xl p-5 shadow-2xl" data-id="element-1247" data-mp="3nwqpy5pmwzdt">
            <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-neutral-400 uppercase" data-id="element-1248" data-mp="1fmhef8fuhh5f">
              <PieChart size={14} className="text-[#EA580C]" data-id="element-1249" data-mp="1kglpo3w58bcb" />
              Analytics & BI
            </div>
            <div className="space-y-2" data-id="element-1250" data-mp="1bmgr0yarpvm8">
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/5" data-id="element-1251" data-mp="1bnig46omcimy">
                <span className="text-sm text-white" data-id="element-1252" data-mp="1zcjh58v4gri7">Executive Dashboard</span>
                <div className="flex items-end gap-0.5 h-4" data-id="element-1253" data-mp="23cljzlclfqz2">
                  <div className="w-1 bg-neutral-500 h-[40%]" data-id="element-1254" data-mp="1pdu7u4hakfdk" />
                  <div className="w-1 bg-neutral-500 h-[70%]" data-id="element-1255" data-mp="1pdc7owridh0d" />
                  <div className="w-1 bg-neutral-500 h-[50%]" data-id="element-1256" data-mp="1uc5hof7l6pvs" />
                  <div className="w-1 bg-[#EA580C] h-[100%]" data-id="element-1257" data-mp="1uerna9ax2vbd" />
                </div>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/5" data-id="element-1258" data-mp="ixry3mjv8wlk">
                <span className="text-sm text-white" data-id="element-1259" data-mp="bdr4tfr4kom2">Customer Insights</span>
                <div className="flex items-end gap-0.5 h-4" data-id="element-1260" data-mp="bg3hpei108s9">
                  <div className="w-1 bg-[#EA580C] h-[80%]" data-id="element-1261" data-mp="6enxtprl48bg" />
                  <div className="w-1 bg-neutral-500 h-[40%]" data-id="element-1262" data-mp="kfgtwexjbbgo" />
                  <div className="w-1 bg-neutral-500 h-[60%]" data-id="element-1263" data-mp="kckxiv1c4uuc" />
                  <div className="w-1 bg-neutral-500 h-[30%]" data-id="element-1264" data-mp="fh5k2478p0yg" />
                </div>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/5" data-id="element-1265" data-mp="3nw765bjpmkqt">
                <span className="text-sm text-white" data-id="element-1266" data-mp="3jvwy7tccxk9b">Reports & Exports</span>
                <div className="flex items-end gap-0.5 h-4" data-id="element-1267" data-mp="1gut715xjmda">
                  <div className="w-1 bg-neutral-500 h-[30%]" data-id="element-1268" data-mp="3sv0hoyffs1oc" />
                  <div className="w-1 bg-neutral-500 h-[50%]" data-id="element-1269" data-mp="3ssnv27yqh34y" />
                  <div className="w-1 bg-[#EA580C] h-[90%]" data-id="element-1270" data-mp="3544nxvrf6weg" />
                  <div className="w-1 bg-neutral-500 h-[60%]" data-id="element-1271" data-mp="306ctov3ayy73" />
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-neutral-500 hover:text-white cursor-pointer transition-colors" data-id="element-1272" data-mp="143q7v2oqw8hn">
              View all dashboards <ArrowRight size={12} data-id="element-1273" data-mp="1dsrnusvaipvm" />
            </div>
          </div>

          {/* Floating Icons / Nodes */}
          <div className="absolute top-[180px] left-[250px] w-10 h-10 rounded-full bg-[#EA580C]/20 border border-[#EA580C] flex items-center justify-center shadow-[0_0_20px_rgba(234,88,12,0.4)]" data-id="element-1274" data-mp="1irl42t1youzh">
            <Database size={16} className="text-[#EA580C]" data-id="element-1275" data-mp="q2xckfqmd9dh" />
          </div>
          <div className="absolute top-[200px] right-[320px] w-10 h-10 rounded-full bg-white/10 border border-white/30 flex items-center justify-center" data-id="element-1276" data-mp="q59uiswdnjqj">
            <Lock size={16} className="text-white" data-id="element-1277" data-mp="z4wst5tt89fd" />
          </div>
          <div className="absolute bottom-[300px] right-[280px] w-10 h-10 rounded-full bg-[#EA580C]/20 border border-[#EA580C] flex items-center justify-center shadow-[0_0_20px_rgba(234,88,12,0.4)]" data-id="element-1278" data-mp="26hp4ysnrmgms">
            <PieChart size={16} className="text-[#EA580C]" data-id="element-1279" data-mp="3v0tb9cevphgb" />
          </div>
          <div className="absolute bottom-[250px] left-[420px] w-12 h-12 rounded-full bg-[#EA580C] flex items-center justify-center shadow-[0_0_30px_rgba(234,88,12,0.6)] z-30" data-id="element-1280" data-mp="3v2w0vvc4d8ki">
            <Share2 size={20} className="text-white" data-id="element-1281" data-mp="3m6wsm7kxan5n" />
          </div>
          <div className="absolute bottom-[280px] left-[80px] w-8 h-8 rounded-full bg-[#1A1410] border border-neutral-600 flex items-center justify-center z-30" data-id="element-1282" data-mp="3h8lj5xyjieny">
            <Lock size={12} className="text-neutral-400" data-id="element-1283" data-mp="37bgu9qdb853g" />
          </div>
        </div>
      </div>
    </section>;
}